import Portal from "@mui/material/Portal";
import { useCallback, useMemo, useState, } from "react";
import { Node, useNodes, useViewport } from "react-flow-renderer";
import { connect } from "react-redux";
import { NetworkData } from "../../../fstn/network";
import { selectModeData } from "../../../redux/selectors/editor";
import { selectConstants, selectNetwork, selectTags } from "../../../redux/selectors/firebase";
import { IGraphData } from "../../../redux/slices/editor";
import { getTagDisplayValue, getUniqueValues, getVisibleGroupableTagStates } from "../../../utility/constantsTagsUtils";
import { paletteColors } from "../../sheets/conditionalFormatting/DefaultStyles";
import { Tags, Constants } from "../StateTags";
import GroupHighlight from "./GroupHighlight";

interface INodeData
{
    width: number
    height: number
}

interface Props
{
    graph: HTMLDivElement | null;
    graphData: IGraphData,
    tags: Tags,
    constants: Constants,
    network: NetworkData
}
const width = 100;
const height = 100;
export function GroupHighlights({ graphData, graph, network, tags, constants }: Props)
{
    const nodes = useNodes<INodeData>();
    const { zoom } = useViewport();
    const { activeGroup, root } = graphData;

    const getNodesInRoot = (root: string, network: NetworkData) =>
    {
        return Object.keys(network.states || {})
            .filter(state => network.states[state].parent === root)
    }

    const getNodesRect = useCallback((nodeIds: string[]) =>
    {
        const nodesInGroup = nodeIds
            .map((id) => nodes.find((node) => node.id === id))
            .filter(node => node !== undefined) as Node<INodeData>[];

        let min = { x: Infinity, y: Infinity };
        let max = { x: -Infinity, y: -Infinity };

        if (nodesInGroup.length === 0)
        {
            return { x: 0, y: 0, width: 0, height: 0 }
        }

        nodesInGroup.forEach((node) =>
        {
            let w = width;
            let h = height;
            let x = node.position.x;
            let y = node.position.y;
            if (node.data.width)
                w = node.data.width;

            if (node.data.height)
                h = node.data.height;

            min.x = Math.min(min.x, x);
            min.y = Math.min(min.y, y);

            max.x = Math.max(max.x, x + w);
            max.y = Math.max(max.y, y + h);
        });

        const x = min.x;
        const y = min.y;

        const groupWidth = max.x - min.x;
        const groupHeight = max.y - min.y;

        return { x, y, width: groupWidth, height: groupHeight };
    }, [nodes]);
    const graphViewport = graph?.getElementsByClassName("react-flow__viewport")[0];

    const nodesInRoot = useMemo(() => getNodesInRoot(root, network), [root, network]);

    const destinationsFlat = useMemo(() => Array.from(nodesInRoot.reduce((destinations, id) =>
    {
        const node = network.states[id];
        node.transitions.forEach((transition) =>
        {
            if (transition.destination)
                destinations.add(transition.destination);
        })
        return destinations;
    }, new Set<string>())), [nodesInRoot, network]);

    const entryNodes = useMemo(() => nodesInRoot.filter((id) => !destinationsFlat.includes(id)), [nodesInRoot, destinationsFlat]);

    const visibleGroups = useMemo(() => getVisibleGroupableTagStates(
        root, network, tags, constants), [root, network, tags, constants]);

    const activeGroupTagValues = useMemo(() => activeGroup in visibleGroups ? visibleGroups[activeGroup] : {}, [activeGroup, visibleGroups]);

    const mergeGroups = (state: string, group: string[], subGroups: string[][]) =>
    {
        if (group.length > 0)
        {
            const newGroup = subGroups.find(subGroup =>
            {
                return subGroup !== group && subGroup.includes(state)
            });

            if (newGroup)
            {
                newGroup.push(...group);
                group.length = 0;

                return newGroup;
            }
        }

        return group;
    }

    const destinationCount: { [state: string]: number } = useMemo(() =>
    {
        return nodesInRoot.reduce((destinationCount, state) =>
        {
            const stateData = network.states[state];

            stateData.transitions.forEach((transition) =>
            {
                const destinationState = transition.destination ?? "";
                if (transition.destination && transition.trigger && stateData?.meta?.type !== "jump")
                {
                    destinationCount[destinationState] = destinationCount[destinationState] ?? 0;
                    destinationCount[destinationState]++;
                }
            });

            return destinationCount;
        }, {} as { [state: string]: number });
    }, [network.states, nodesInRoot]);

    const subGroupsMap: { [tagValue: string]: string[][] } = useMemo(() =>
    {
        const splitOnBranchSplitOrMerge = graphData.splitGroupsOnBranching;
        const subGroupsMap: { [tagValue: string]: string[][] } = {};

        Object.keys(activeGroupTagValues).forEach((tagValue) =>
        {
            if (graphData.hiddenGroupValues[String(tagValue)])
            {
                return;
            }
            const tagValueNodes = activeGroupTagValues[tagValue];
            const visited: string[] = [];
            const subGroups = subGroupsMap[tagValue] = [] as string[][];

            const addState = (state: string, group: string[]) =>
            {
                const newGroup = () =>
                {
                    group = [];
                    subGroups.push(group);
                }
                let node = network.states[state];
                while (node?.parent === root)
                {
                    if (splitOnBranchSplitOrMerge && (destinationCount[state] > 1 || node.transitions.length > 1))
                    {
                        newGroup();
                    }

                    if (tagValueNodes.includes(state))
                    {
                        if (!visited.includes(state))
                        {
                            group.push(state);
                        }
                        else if (!splitOnBranchSplitOrMerge)
                        {
                            group = mergeGroups(state, group, subGroups);
                        }
                    }
                    else if (group.length > 0)
                    {
                        newGroup();
                    }

                    if (visited.includes(state))
                        break;

                    visited.push(state);

                    if (node?.transitions.length === 1)
                    {
                        const transition = node.transitions[0];
                        if (transition.meta?.type !== "jump")
                        {
                            state = transition.destination ?? "";
                            node = network.states[state];
                        }
                    }
                    else
                    {
                        for (const transition of node.transitions)
                        {
                            splitOnBranchSplitOrMerge && newGroup();
                            if (transition.destination && transition.meta?.type !== "jump")
                            {
                                addState(transition.destination, group);
                            }
                        }
                    }
                }
            }

            entryNodes.forEach((state) =>
            {
                const firstSubGroup: string[] = [];
                subGroups.push(firstSubGroup);
                addState(state, firstSubGroup);
            });
        });

        return subGroupsMap;
    }, [activeGroupTagValues, entryNodes, network.states, root, destinationCount,
        graphData.splitGroupsOnBranching, graphData.hiddenGroupValues]);

    const colourData: { [value: string]: { color: string } } = useMemo(() => Object.values(activeGroup in constants ? (constants[activeGroup].constraints ?? {}) : {})
        .find((constant) => constant.type === "groupable")?.data, [constants, activeGroup]);

    const uniqueValues = useMemo(() => getUniqueValues(activeGroup, tags), [activeGroup, tags]);

    const getColor = useCallback((value: string) =>
    {
        const index = uniqueValues.indexOf(value);
        const defaultColor = paletteColors[index % paletteColors.length];
        value = value.replaceAll(".", "_");
        if (colourData && value in colourData && colourData[value]?.color)
        {
            return colourData[value].color;
        }

        return defaultColor;
    }, [uniqueValues, colourData]);

    const indexMap: { [id: string]: number } = {}
    const [sharedDataMap, setSharedDataMap] = useState<{ [id: string]: { height: number }[] }>({});

    return <>
        {graphViewport && activeGroup && <Portal container={graphViewport}>
            {Object.keys(subGroupsMap).map((tagValue) =>
            {
                const subGroup = subGroupsMap[tagValue];
                return subGroup.map((group, index) =>
                {
                    if (group.length === 0)
                        return null;

                    const rect = getNodesRect(group);

                    const id = group.join(",");
                    indexMap[id] = indexMap[id] ?? 0;
                    const indexHeightMap = ++indexMap[id];

                    return <GroupHighlight key={`${tagValue}_${index}`}
                        index={indexHeightMap}
                        sharedData={sharedDataMap[id]}
                        setTitleHeight={(height: number) =>
                        {
                            setSharedDataMap((data) =>
                            {
                                data[id] = data[id] ?? [];
                                data[id][indexHeightMap] = { height };
                                return data;
                            });
                        }}
                        {...rect}
                        zoom={zoom}
                        color={getColor(tagValue)}
                        title={getTagDisplayValue({ value: tagValue, type: activeGroup }, constants).toString()} />
                })
            })}
        </Portal>
        }
    </>
}

const mapStateToProps = (store: never) => ({
    graphData: selectModeData(store),
    tags: selectTags(store) ?? {},
    constants: selectConstants(store) ?? {},
    network: selectNetwork(store) ?? {}
});
export default connect(mapStateToProps, {
})(GroupHighlights);